import { SxProps, Theme, useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";
import { ObligationPageStatus } from "./ObligationExtraction.types";

/**
 * Colours one pager number by the state of the obligation behind it. Only the selected item keeps the
 * default filled treatment, so the current page still reads as the current page whatever its status.
 */
export function getPaginationItemStyle(status: ObligationPageStatus | undefined): SxProps<Theme> {
    switch (status) {
        case "needsAttention":
            return { color: "error.main", fontWeight: 600, "&.Mui-selected": { backgroundColor: "error.main", color: "common.white" } };
        case "ready":
            return { color: "primary.main", fontWeight: 600, "&.Mui-selected": { backgroundColor: "primary.main", color: "common.white" } };
        case "created":
            return { color: "primary.main", opacity: 0.7 };
        case "excluded":
            return { color: "primary.main", textDecoration: "line-through", opacity: 0.5 };
        default:
            return {};
    }
}

export const useObligationExtractionPanelStyles = () => {
    const theme = useTheme();
    return createSxStyles({
        // The field tooltips are absolutely positioned ~39px to the right of their input, so the right
        // padding has to reserve that room or they are cut off by the panel edge.
        body: {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: 2,
            paddingRight: 2,
        },
        // An excluded page stays readable and editable so the user can judge it before including it again.
        excludedBody: {
            opacity: 0.55,
        },
        footer: {
            padding: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            gap: 1,
        },
        headerRow: {
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
        },
        footerActions: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
        },
        disclaimer: {
            textAlign: "center",
        },
        pager: {
            display: "flex",
            justifyContent: "center",
        },
        summary: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
        },
        summarySeparator: {
            color: "text.disabled",
        },
        // A button rather than a link: it moves the panel to a page, it does not navigate anywhere.
        summaryAction: {
            padding: 0,
            minWidth: "auto",
            font: "inherit",
            color: "error.main",
            textTransform: "none",
            textDecoration: "underline",
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
        },
        centeredState: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            padding: 3,
            textAlign: "center",
            height: "100%",
        },
        obligationStatusCounts: {
            fontSize: theme.typography.pxToRem(12),
            color: theme.palette.primary.dark,
        },
    });
};
